import React, { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { Form, Row, Col, Alert, Spinner } from "react-bootstrap";
import WorkService from "../../../services/WorkService";

const ThermoformingForm = forwardRef(({ patientId, instanceId, actionId, onValidityChange }, ref) => {
  const [material, setMaterial] = useState(() => {
    return localStorage.getItem("om_thermoform_material") || "";
  });
  const [duration, setDuration] = useState(() => {
    return localStorage.getItem("om_thermoform_duration") || "";
  });
  const [hasPrintRequest, setHasPrintRequest] = useState(true);
  const [checking, setChecking] = useState(true);
  const [dbError, setDbError] = useState("");
  const [error, setError] = useState("");

  // Check print request on mount/patient change
  useEffect(() => {
    async function checkPrintRequest() {
      try {
        setChecking(true);
        console.log("Checking print request for patientId:", patientId, "instanceId:", instanceId);
        
        const query = `
          SELECT TOP 1 Id, PatientId, PlanId 
          FROM tbl_PRINTREQUEST 
          WHERE PatientId = ${Number(patientId || 0)}
             OR PlanId = ${Number(instanceId || 0)}
          ORDER BY Id DESC
        `;
        
        const res = await WorkService.runQuery(query);
        console.log("Print request query result:", res);

        if (!res || res.length === 0) {
          setHasPrintRequest(false);
          setDbError(`Warning: No print request found for this patient (Patient ID: ${patientId || "N/A"}, Plan ID: ${instanceId || "N/A"}). You cannot complete this step without a print request.`);
        } else {
          setHasPrintRequest(true);
          setDbError("");
        }
      } catch (err) {
        console.error("Error checking print request:", err);
        setDbError("Failed to verify print request status in database.");
      } finally {
        setChecking(false);
      }
    }
    if (patientId || instanceId) {
      checkPrintRequest();
    }
  }, [patientId, instanceId]);

  // Propagate validity to parent
  useEffect(() => {
    const isValid = !checking && hasPrintRequest && material.trim() !== "" && duration.trim() !== "";
    if (onValidityChange) {
      onValidityChange(isValid);
    }
  }, [checking, hasPrintRequest, material, duration, onValidityChange]);

  useImperativeHandle(ref, () => ({
    validate: () => {
      if (!hasPrintRequest) {
        setError("Cannot complete step: No print request exists for this patient.");
        return false;
      }
      if (!material.trim()) {
        setError("Material is required.");
        return false;
      }
      if (!duration.trim()) {
        setError("Duration is required.");
        return false;
      }
      setError("");
      return true;
    },
    onSave: async () => {
      // Save to localStorage (Approach A technician-global cache)
      localStorage.setItem("om_thermoform_material", material.trim());
      localStorage.setItem("om_thermoform_duration", duration.trim());

      // Save to DB (update Note in tbl_PRINTREQUEST by concatenating material and duration)
      const concatVal = `[Material: ${material.trim()}, Duration: ${duration.trim()}]`;
      const concatValSql = `'${concatVal.replace(/'/g, "''")}'`;

      const query = `
        UPDATE tbl_PRINTREQUEST 
        SET Note = CAST(ISNULL(Note, '') AS VARCHAR(MAX)) + ' ' + ${concatValSql}
        WHERE Id = (
          SELECT TOP 1 Id 
          FROM tbl_PRINTREQUEST 
          WHERE PatientId = ${Number(patientId || 0)}
             OR PlanId = ${Number(instanceId || 0)}
          ORDER BY Id DESC
        )
      `;

      return WorkService.runQuery(query);
    }
  }));

  return (
    <div className="p-3 bg-white rounded-4 border border-light shadow-sm mb-4 animate-fade-in">
      <h5 className="fw-bold mb-3 text-primary">Thermoforming Specifications</h5>
      
      {checking && (
        <div className="text-center py-2">
          <Spinner animation="border" size="sm" variant="primary" className="me-2" />
          <span className="text-muted small">Checking print request status...</span>
        </div>
      )}

      {dbError && (
        <Alert variant={hasPrintRequest ? "danger" : "warning"} className="py-2 px-3 small border-0 shadow-sm rounded-3 mb-3">
          <i className="fa fa-exclamation-triangle me-2"></i>
          {dbError}
        </Alert>
      )}

      {error && <div className="text-danger small mb-2">{error}</div>}

      <Row>
        <Col xs={12} sm={6} className="mb-2">
          <Form.Group>
            <Form.Label className="fw-semibold text-secondary small">Material</Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g. PET-G 1.0mm"
              value={material}
              disabled={checking || !hasPrintRequest}
              onChange={(e) => {
                setMaterial(e.target.value);
                if (error) setError("");
              }}
              className="rounded-pill px-3"
            />
          </Form.Group>
        </Col>

        <Col xs={12} sm={6} className="mb-2">
          <Form.Group>
            <Form.Label className="fw-semibold text-secondary small">Thermoform Duration</Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g. 30s"
              value={duration}
              disabled={checking || !hasPrintRequest}
              onChange={(e) => {
                setDuration(e.target.value);
                if (error) setError("");
              }}
              className="rounded-pill px-3"
            />
          </Form.Group>
        </Col>
      </Row>
    </div>
  );
});

export default ThermoformingForm;
